import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import BookOnlineRoundedIcon from "@mui/icons-material/BookOnlineRounded";
import { useApp } from "../../../context";
import PageTemplate from "../../components/PageTemplate";
import { normalizeBooking } from "../../../utils/booking";
import { formatMoney } from "../../../utils/pricing";

const Main = styled("div")(({ theme }) => ({
  flexGrow: 1,
  minHeight: "100vh",
  padding: theme.spacing(2),
  background: theme.palette.background.default,
}));

const BookingCard = styled(Paper)(({ theme }) => ({
  ...theme.typography.body1,
  display: "block",
  textAlign: "left",
  textDecoration: "none",
  color: theme.palette.text.secondary,
  padding: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  transition: theme.transitions.create(["box-shadow", "border-color"]),
  "&:hover": { boxShadow: theme.shadows[4], borderColor: theme.palette.primary.main },
  "&:focus-visible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
}));

const formatBookingDates = (start, end) => {
  if (!start) return "";
  const startStr = moment(start).format("DD MMM YYYY");
  if (!end || start.getTime() === end.getTime()) return startStr;
  return `${startStr} - ${moment(end).format("DD MMM YYYY")}`;
};

export default function BookingListPage() {
  const { bookings: rawBookings, getAllBookings, user } = useApp();

  useEffect(() => {
    if (user && user.uid) {
      getAllBookings(user.uid);
    }
    // `getAllBookings` is unmemoised on the app context and sets state, so
    // listing it here loops. Keyed on the identity that actually matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Normalize once so this page renders legacy and v2 documents identically.
  const bookings = React.useMemo(
    () =>
      (rawBookings || [])
        .map((b) => normalizeBooking(b))
        .filter(Boolean)
        .sort((a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0)),
    [rawBookings]
  );

  const hasBookings = bookings.length > 0;

  return (
    <PageTemplate>
      <Main>
        <Toolbar sx={{ marginBottom: "1em" }} />
        <Box sx={{ maxWidth: 800, mx: "auto" }}>
          <Typography component="h1" className="txt--gray" sx={{ mb: 2 }}>
            Your Bookings
          </Typography>

          {hasBookings ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {bookings.map((booking) => (
                <BookingCard key={booking.id} component={Link} to={`/booking/${booking.id}`} elevation={1}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h6" component="h2" color="text.primary" sx={{ display: "flex", alignItems: "center" }}>
                        <BookOnlineRoundedIcon sx={{ fontSize: "1.1rem", mr: 0.5, color: "text.secondary", flexShrink: 0 }} />
                        {booking.listingName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {formatBookingDates(booking.startDate, booking.endDate)}
                      </Typography>
                      <Typography variant="body2" color="text.primary" sx={{ mt: 0.5, fontWeight: 600 }}>
                        {formatMoney(booking.totalPaid, booking.currency)}
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                          {` · ${booking.guests} guest${booking.guests === 1 ? "" : "s"}`}
                          {booking.nights > 1 ? ` · ${booking.nights} nights` : ""}
                        </Typography>
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
                        {booking.bookingType && <Chip label={booking.bookingType} size="small" color="primary" variant="outlined" />}
                        {booking.status && <Chip label={booking.status} size="small" variant="outlined" />}
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", color: "primary.main", flexShrink: 0 }}>
                      <Typography variant="button">View</Typography>
                      <ChevronRightRoundedIcon fontSize="small" />
                    </Box>
                  </Box>
                </BookingCard>
              ))}
            </Box>
          ) : (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography variant="h6" color="text.primary" sx={{ mb: 0.5 }}>
                No bookings found.
              </Typography>
              <Typography color="text.secondary">
                Your saved bookings will appear here.
              </Typography>
            </Box>
          )}
        </Box>
      </Main>
    </PageTemplate>
  );
}
